import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React, { useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import { SelectedItemDetails } from '@/components/modules/trip-route-details/trip-route-details';
import TripRouteDetailsModal from '@/components/modules/trip-route-details/trip-route-details-modal/trip-route-details-modal';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import Button from '@/components/molecules/button/button';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './trip-route-details-description.module.scss';

export interface TripRouteDetailsDescriptionProps {
  /** The details to show on the card */
  details: SelectedItemDetails;
  /** The function that gets triggered on navigation */
  onClick?: (id: number) => void;
}

const TripRouteDetailsDescription = ({
  details,
  onClick,
}: TripRouteDetailsDescriptionProps) => {
  const { t } = useI18n();
  const isTablet = useWindowSize('tablet');
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  function hasValidButtons(
    details: SelectedItemDetails,
  ): details is Required<
    Pick<SelectedItemDetails, 'currentCardIndex' | 'totalItemsCount'>
  > {
    return (
      details.currentCardIndex !== undefined &&
      details.totalItemsCount !== undefined
    );
  }

  const handleReadMore = () => {
    setIsDescriptionModalOpen(true);
  };

  const handleNavigation = (value: number) => {
    onClick?.(value);
  };

  return (
    <div
      className={classNames(styles.component, {
        [styles.isHidden]: !details.id,
      })}
    >
      <div className={styles.card}>
        {isTablet && details.images && (
          <div className={styles.imageWrapper}>
            <ImageSlider
              className={styles.imageSlider}
              compact
              images={details.images}
            />
          </div>
        )}

        <div className={styles.contentWrapper}>
          <div className={styles.header}>
            {details.headline && (
              <Typography className={styles.headline} tagStyle="headingMedium">
                {details.headline}
              </Typography>
            )}

            {hasValidButtons(details) && (
              <div className={styles.buttonsWrapper}>
                <Button
                  disabled={details.currentCardIndex === 0}
                  icon="ChevronLeft"
                  isSmall={!isTablet}
                  onClick={() => handleNavigation(details.currentCardIndex - 1)}
                  variant="tertiary"
                />

                <Button
                  disabled={
                    details.currentCardIndex === details.totalItemsCount - 1
                  }
                  icon="ChevronRight"
                  isSmall={!isTablet}
                  onClick={() => handleNavigation(details.currentCardIndex + 1)}
                  variant="tertiary"
                />
              </div>
            )}
          </div>

          <div className={styles.description}>
            {details.subHeadline && (
              <Typography
                className={styles.subHeadline}
                tagStyle="bodyMediumBold"
              >
                {details.subHeadline}
              </Typography>
            )}

            {details.text && (
              <RichText className={styles.text} text={details.text} />
            )}
          </div>

          <div className={styles.action}>
            <AnchorLink
              className={styles.link}
              icon="ArrowRight"
              onClick={handleReadMore}
              text={t('items.common.words.readMore')}
            />
          </div>
        </div>
      </div>

      <TripRouteDetailsModal
        details={details}
        isOpen={isDescriptionModalOpen}
        setIsOpen={value => setIsDescriptionModalOpen(value)}
      />
    </div>
  );
};

TripRouteDetailsDescription.displayName = 'TripRouteDetailsDescription';

export default TripRouteDetailsDescription;
